import { z } from "zod";
/**
 * The three questions this application asks a language model, and whether
 * each is asked at all. Decided 2026-09-28 in `project-scope.md`.
 *
 * `windowHelp` is the help with a wine's four drinking-window years — the
 * button on the wine's page, not the window itself, which is the cellar's own
 * record and is never switched off. `meal` is *Till maten*. `quality` is the
 * quality report, its page and its history.
 */
export const featuresSchema = z.object({
    windowHelp: z.boolean(),
    meal: z.boolean(),
    quality: z.boolean(),
    /**
     * *Upptäck* — reading the published area reports and wishing for one.
     * Added the same evening; not a question to the model, since only the
     * administrator runs a report, but switched the same way so the
     * administrator decides who is shown it.
     *
     * Defaulted for one release, and made required on 2026-09-29 once revision
     * 64 was serving it. Left defaulted, an API that stopped sending it would
     * read as *Upptäck on for everybody* — the one wrong answer that looks
     * right.
     */
    discover: z.boolean(),
});
/** What an account is allowed when nothing has been switched off. */
export const ALL_FEATURES = {
    windowHelp: true,
    meal: true,
    quality: true,
    discover: true,
};
/**
 * The six switches, as the administrator's page reads and writes them: the
 * three questions for the administrator's own account, and the same three for
 * every other account in every cellar.
 *
 * The request and the response are one shape, because saving is sending the
 * whole page back — six booleans are cheaper to send whole than to describe
 * as a change, and a whole write cannot half-land.
 */
export const systemSettingsSchema = z.object({
    administrator: featuresSchema,
    others: featuresSchema,
    /**
     * The fourth question — describing a wine area — for the administrator
     * alone, added 2026-09-28. **One switch, not two**: nobody else may run the
     * question yet, and an *everybody else* switch would decide nothing, so it
     * is not in `featuresSchema` and has no cellar override. See
     * `project-scope.md`.
     *
     * Defaulted for one release, both ways round, and made required on
     * 2026-09-29 once the API had been sending it since revision 62.
     */
    areaReports: z.boolean(),
    /**
     * The weekly search for where the wishes are sold, allowed or not — added
     * 2026-10-17. Defaulted to *allowed* for one release, matching the column,
     * so a browser ahead of the API saves what the API would have said.
     */
    wishSearch: z.boolean().default(true),
});
/**
 * One cellar's answer to each question, replacing *everybody else* there.
 * Null is *som alla andra*. Added the same day; see `project-scope.md`.
 */
export const cellarOverridesSchema = z.object({
    windowHelp: z.boolean().nullable(),
    meal: z.boolean().nullable(),
    quality: z.boolean().nullable(),
    /** Defaulted for one release and made required with
     *  `featuresSchema.discover`. */
    discover: z.boolean().nullable(),
});
/** No override at all — every question as for everybody else. */
export const NO_OVERRIDES = {
    windowHelp: null,
    meal: null,
    quality: null,
    discover: null,
};
/** Every cellar in the system, as the administrator's page lists them. */
export const systemCellarSchema = z.object({
    id: z.string(),
    name: z.string(),
    ownerName: z.string(),
    overrides: cellarOverridesSchema,
});
export const systemCellarListResponseSchema = z.object({
    cellars: z.array(systemCellarSchema),
});
/**
 * Every account, and when it last signed in and was last seen — added
 * 2026-09-29, asked for by the owner; see `project-scope.md`. The
 * administrator's alone.
 *
 * **Read from the sessions, not stamped anew**: a session is created by a
 * sign-in and never deleted, so the newest one's creation is the last
 * sign-in and the latest `lastSeenAt` among them the last time the account
 * was used, to a quarter of an hour. Null means never — an account
 * provisioned and not yet used.
 */
export const systemAccountSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    isAdministrator: z.boolean(),
    createdAt: z.string(),
    lastSignedInAt: z.string().nullable(),
    lastSeenAt: z.string().nullable(),
});
export const systemAccountListResponseSchema = z.object({
    accounts: z.array(systemAccountSchema),
});
//# sourceMappingURL=system.js.map